import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  Shield, Users, BarChart3, Loader2, Target, Ticket,
  TrendingUp, AlertTriangle, DollarSign, Eye
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth, API } from "@/App";
import DashboardLayout from "@/components/DashboardLayout";

const AdminPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [stats, setStats] = useState(null);
  const [books, setBooks] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, booksRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true, headers }),
        axios.get(`${API}/earn/admin/books`, { withCredentials: true, headers }).catch(() => null)
      ]);
      setStats(statsRes.data);
      if (booksRes) setBooks(booksRes.data);
      setAccessDenied(false);
    } catch (error) {
      if (error.response?.status === 403) setAccessDenied(true);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout currentPage="admin">
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-aura-emerald" />
        </div>
      </DashboardLayout>
    );
  }

  if (accessDenied) {
    return (
      <DashboardLayout currentPage="admin">
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-red-400" />
            </div>
            <h2 className="font-heading text-2xl font-bold text-white mb-2">Access Denied</h2>
            <p className="text-aura-text-secondary mb-6">You don't have permission to access the admin panel.</p>
            <Button onClick={() => navigate("/dashboard")} className="btn-primary">Back to Dashboard</Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const paidUsers = books?.subscribers?.total_paid ?? stats?.premium_users ?? 0;
  const freeUsers = books?.subscribers?.total_free ?? (stats ? stats.total_users - stats.premium_users : 0);
  const weeklyEarnings = paidUsers * 3.50;
  const monthlyEarnings = weeklyEarnings * 4.33;
  const yearlyEarnings = weeklyEarnings * 52;
  const conversionRate = stats?.total_users > 0 ? ((paidUsers / stats.total_users) * 100).toFixed(1) : "0.0";

  return (
    <DashboardLayout currentPage="admin">
      <div className="p-6 max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center">
              <Shield className="w-6 h-6 text-red-400" />
            </div>
            <div>
              <h1 className="font-heading text-3xl font-bold text-white">Business Overview</h1>
              <p className="text-aura-text-secondary">Operator dashboard for AI Manifest / AURA</p>
            </div>
          </div>
          <div className="text-sm text-aura-text-muted">
            Logged in as: <span className="text-aura-emerald">{user?.email}</span>
          </div>
        </div>

        {/* Core Metrics */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card icon={Users} label="Total Users" value={stats.total_users} accent="emerald" delay={0} />
            <Card icon={TrendingUp} label="AURA PRO Users" value={paidUsers} accent="gold" delay={0.05} />
            <Card icon={Target} label="Total Predictions" value={stats.total_predictions} accent="purple" delay={0.1} />
            <Card icon={Ticket} label="Games Tracked" value={stats.total_tickets} accent="blue" delay={0.15} />
          </div>
        )}

        {/* Visitor Analytics — Coming Soon */}
        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="card-aura p-4">
          <div className="flex items-center gap-2 mb-2">
            <Eye className="w-4 h-4 text-yellow-400" />
            <h2 className="text-white font-semibold text-sm">Visitor Analytics</h2>
            <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-500/10 text-yellow-400">Coming Soon</span>
          </div>
          <p className="text-xs text-aura-text-muted">Connect Google Analytics or PostHog to populate live visitor data.</p>
        </motion.div>

        {/* Affiliate Overview */}
        {books && (
          <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className="card-aura p-5">
            <div className="flex items-center gap-2 mb-4">
              <Users className="w-5 h-5 text-purple-400" />
              <h2 className="text-white font-semibold">Affiliate Overview</h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <Stat label="Total Affiliates" value={books.subscribers?.total ? "—" : "0"} />
              <Stat label="Monthly Payout Due" value={`$${(books.affiliate_liabilities?.eligible_payouts || 0).toFixed(2)}`} />
              <Stat label="Paid to Date" value={`$${(books.affiliate_liabilities?.paid_payouts || 0).toFixed(2)}`} />
            </div>
          </motion.div>
        )}

        {/* Conversion & Earnings */}
        <div className="grid md:grid-cols-2 gap-4">
          <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className="card-aura p-5">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-5 h-5 text-aura-emerald" />
              <h2 className="text-white font-semibold">Conversion</h2>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Stat label="Free → PRO Rate" value={`${conversionRate}%`} />
              <Stat label="Free Users" value={freeUsers} />
              <Stat label="AURA PRO Users" value={paidUsers} />
              <Stat label="Total Users" value={stats?.total_users ?? 0} />
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="card-aura p-5">
            <div className="flex items-center gap-2 mb-4">
              <DollarSign className="w-5 h-5 text-aura-gold" />
              <h2 className="text-white font-semibold">Earnings</h2>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Stat label="Weekly" value={`$${weeklyEarnings.toFixed(2)}`} />
              <Stat label="Monthly" value={`$${monthlyEarnings.toFixed(2)}`} />
              <Stat label="Yearly" value={`$${yearlyEarnings.toFixed(2)}`} />
              <Stat label="Forecast (12m)" value={`$${yearlyEarnings.toFixed(2)}`} />
            </div>
          </motion.div>
        </div>
      </div>
    </DashboardLayout>
  );
};

const Card = ({ icon: Icon, label, value, accent, delay }) => {
  const colors = { emerald: "text-aura-emerald", gold: "text-aura-gold", purple: "text-purple-400", blue: "text-blue-400" };
  return (
    <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }} className="card-aura p-4" data-testid={`admin-metric-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <Icon className={`w-6 h-6 ${colors[accent]} mb-2`} />
      <p className="text-2xl font-bold text-white">{value}</p>
      <p className="text-sm text-aura-text-muted">{label}</p>
    </motion.div>
  );
};

const Stat = ({ label, value }) => (
  <div className="bg-white/5 rounded-lg p-3 text-center">
    <p className="text-lg font-semibold text-white">{value}</p>
    <p className="text-xs text-aura-text-muted">{label}</p>
  </div>
);

export default AdminPage;
